/**
 * Central datetime formatting helpers for Vantio frontend (Sprint A.5 Step 4-5).
 *
 * Conventions:
 * - Locale: de-CH (Swiss German number/date conventions).
 * - Timezone: Europe/Zurich (handles CEST/CET automatically via Intl).
 * - Input: ISO datetime strings from the backend (uniformly Z-suffix UTC
 *   post-Sprint-A.5 — no client-side normalization needed).
 * - Date-typed fields (`YYYY-MM-DD`) are handled by `formatDateOnly`
 *   directly — no Date construction, no timezone shift.
 *
 * Cross-refs: backend docs/TIMEZONE_ARCHITECTURE.md §4-§5;
 *             CLAUDE.md Regel 30-31.
 */

export const ZURICH_TZ = "Europe/Zurich";
export const VANTIO_LOCALE = "de-CH";

/**
 * Format an ISO datetime string (Z-suffix UTC from backend) as
 * "DD.MM.YYYY, HH:mm" in Zurich wall-clock.
 *
 * Returns "—" for null/empty/undefined input.
 * Returns the raw input string if it cannot be parsed as a Date.
 */
export function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(VANTIO_LOCALE, {
    timeZone: ZURICH_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format a date — either a YYYY-MM-DD string (backend date-typed wire
 * format) or an ISO datetime — as "DD.MM.YYYY".
 *
 * For YYYY-MM-DD: split + reorder (no Date / no timezone shift).
 * For ISO datetime: render in Zurich-CEST wall-clock.
 *
 * Returns "—" for null/empty/undefined input.
 * Returns the raw input string if it cannot be parsed.
 */
export function formatDateOnly(iso) {
  if (!iso) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  }
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString(VANTIO_LOCALE, {
    timeZone: ZURICH_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}


/* ──────────────────────────────────────────────────────────────────────
 * Business timezone — input/serialization side.
 *
 * Everything above formats an instant FOR DISPLAY in Zurich. The block
 * below is the missing other half: turning what the user TYPED into an
 * instant, and back again. Without it the app had two clocks — the
 * display pinned to Europe/Zurich, the form pinned to whatever zone the
 * device happened to be in — and they only agreed for users sitting in
 * Switzerland.
 *
 * The rule these implement: a time a user enters is a SWISS wall clock
 * time. 14:00 typed in Istanbul means 14:00 in Zurich, not 14:00 local.
 * The device's zone carries no business meaning anywhere in Vantio.
 *
 * DST is handled by the platform's IANA database via `Intl`, never by
 * arithmetic here: there is no +1/+2 anywhere below, and no table of
 * transition dates to go stale.
 * ────────────────────────────────────────────────────────────────────── */

/** Formatter cache — these are expensive to construct and the calendar
 *  converts once per event per render. */
const _partsFormatters = new Map();

function _partsFormatter(timeZone) {
  let fmt = _partsFormatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", {
      timeZone,
      // h23 (not hour12:false) — some ICU builds report midnight as "24"
      // under hour12:false, which would put every all-day boundary on
      // the wrong day.
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    _partsFormatters.set(timeZone, fmt);
  }
  return fmt;
}

/**
 * Wall-clock parts of an absolute instant in `timeZone`.
 * `month` is 1-based, matching how humans and ISO strings write it.
 */
export function zonedPartsAtInstant(ms, timeZone = ZURICH_TZ) {
  const out = {};
  for (const p of _partsFormatter(timeZone).formatToParts(new Date(ms))) {
    if (p.type !== "literal") out[p.type] = p.value;
  }
  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour: Number(out.hour) % 24,
    minute: Number(out.minute),
    second: Number(out.second),
  };
}

/**
 * Offset of `timeZone` at an absolute instant, in minutes east of UTC
 * (Zurich: +60 in winter, +120 in summer — read from the platform, not
 * assumed).
 *
 * Works by re-reading the zone's wall clock as if it were UTC: the gap
 * between that and the real instant IS the offset.
 */
export function zoneOffsetMinutesAtInstant(ms, timeZone = ZURICH_TZ) {
  const p = zonedPartsAtInstant(ms, timeZone);
  const wallAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // Parts resolve to whole seconds; floor so sub-second input can't leak
  // into the difference. Zone offsets are whole minutes.
  return (wallAsUtc - Math.floor(ms / 1000) * 1000) / 60000;
}

/**
 * The instant at which `timeZone` shows the given wall clock.
 *
 * Two passes, because the offset we need depends on the answer we are
 * computing: the first pass reads the offset near the target, the second
 * re-reads it at the corrected instant. That second read is what makes
 * the DST changeover days come out right instead of being an hour off.
 */
export function wallClockToInstantMs(parts, timeZone = ZURICH_TZ) {
  const wallAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour || 0,
    parts.minute || 0,
    parts.second || 0
  );
  const first = zoneOffsetMinutesAtInstant(wallAsUtc, timeZone) * 60000;
  const second = zoneOffsetMinutesAtInstant(wallAsUtc - first, timeZone) * 60000;
  return wallAsUtc - second;
}

const _pad2 = (n) => String(n).padStart(2, "0");

/** "YYYY-MM-DDTHH:mm" / "YYYY-MM-DD" → parts, or null. Deliberately not
 *  `new Date(...)`: that is the browser-local parse this module exists
 *  to avoid. */
function _parseWallClock(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(
    String(value)
  );
  if (!m) return null;
  const parts = {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: m[4] === undefined ? 0 : Number(m[4]),
    minute: m[5] === undefined ? 0 : Number(m[5]),
    second: m[6] === undefined ? 0 : Number(m[6]),
  };
  if (parts.month < 1 || parts.month > 12 || parts.day < 1 || parts.day > 31) {
    return null;
  }
  if (parts.hour > 23 || parts.minute > 59 || parts.second > 59) return null;
  return parts;
}

/**
 * `<input type="datetime-local">` value (a SWISS wall clock) → UTC ISO
 * string for the wire. `""`/invalid → null.
 *
 * One hour a year is genuinely ambiguous: when Switzerland falls back,
 * the wall clock 02:30 happens twice, and a datetime-local field holds
 * "02:30" and nothing else. Both readings are defensible; this resolves
 * to the SECOND pass (standard time), consistently. Every timezone
 * library has to pick here too — it is a property of writing an instant
 * as a wall clock, not something a different algorithm would avoid.
 * Pinned in taskFormState.businessTimezone.test.js.
 */
export function businessLocalToIso(value) {
  if (!value) return null;
  const parts = _parseWallClock(value);
  if (!parts) return null;
  const ms = wallClockToInstantMs(parts);
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toISOString();
}

/**
 * Separate `<input type="date">` + `<input type="time">` values (a SWISS
 * wall clock) → UTC ISO string. Both required; otherwise null.
 */
export function businessDateTimeToIso(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  return businessLocalToIso(`${dateStr}T${timeStr}`);
}

/**
 * `<input type="date">` value → the UTC ISO instant of SWISS midnight
 * that day. For day-boundary filters, where "from the 18th" has to mean
 * the Swiss 18th for every user.
 */
export function businessDateToIso(dateStr) {
  if (!dateStr) return null;
  return businessLocalToIso(`${dateStr}T00:00`);
}

/** Instant (Date | ms | ISO string) → ms, or NaN. */
function _toMs(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  return new Date(value).getTime();
}

/** Instant → "YYYY-MM-DDTHH:mm" in SWISS wall clock, for a
 *  `<input type="datetime-local">`. */
export function instantToBusinessLocalInput(value) {
  const ms = _toMs(value);
  if (Number.isNaN(ms)) return "";
  const p = zonedPartsAtInstant(ms);
  return (
    `${p.year}-${_pad2(p.month)}-${_pad2(p.day)}` +
    `T${_pad2(p.hour)}:${_pad2(p.minute)}`
  );
}

/** Instant → "YYYY-MM-DD" in SWISS wall clock (the civil date the
 *  backend derives `work_date` from). */
export function instantToBusinessDate(value) {
  const ms = _toMs(value);
  if (Number.isNaN(ms)) return "";
  const p = zonedPartsAtInstant(ms);
  return `${p.year}-${_pad2(p.month)}-${_pad2(p.day)}`;
}

/** Instant → "HH:mm" in SWISS wall clock, for an `<input type="time">`. */
export function instantToBusinessTime(value) {
  const ms = _toMs(value);
  if (Number.isNaN(ms)) return "";
  const p = zonedPartsAtInstant(ms);
  return `${_pad2(p.hour)}:${_pad2(p.minute)}`;
}

/** Backend ISO → `<input type="datetime-local">` value. `null`/invalid → "". */
export function isoToBusinessLocalInput(iso) {
  return iso ? instantToBusinessLocalInput(iso) : "";
}

/** Backend ISO → `<input type="date">` value. */
export function isoToBusinessDateInput(iso) {
  return iso ? instantToBusinessDate(iso) : "";
}

/** Backend ISO → `<input type="time">` value. */
export function isoToBusinessTimeInput(iso) {
  return iso ? instantToBusinessTime(iso) : "";
}

/**
 * Today's civil date in Switzerland, "YYYY-MM-DD".
 *
 * The ONE definition of "heute" for the whole frontend. Deliberately not the
 * UTC calendar day taken off an ISO string: between Zurich midnight and UTC
 * midnight that names YESTERDAY — every night from 22:00Z in summer and
 * 23:00Z in winter. And deliberately not the device's own calendar fields,
 * which are correct for a user sitting in Switzerland and wrong for the same
 * user in Istanbul or New York. Vantio's business day is Swiss for everyone,
 * everywhere.
 *
 * `now` is injectable so a test can pin a boundary instant instead of waiting
 * for one. Omitting it reads the clock, which is what every caller does.
 */
export function todayBusinessDate(now) {
  return instantToBusinessDate(now === undefined ? Date.now() : now);
}

/**
 * The current Swiss business period as `{ year, month }` (month 1-based).
 *
 * The year/month counterpart to `todayBusinessDate`, for the surfaces that
 * ask "which reporting month are we in?" rather than "which day is it?".
 * Derived from the same zone conversion, so the two can never disagree about
 * where a month boundary falls.
 *
 * Both fields come from ONE reading of the clock. Two separate reads — a
 * `.getFullYear()` next to a `.getMonth()` — can straddle midnight on 31
 * December and produce a period that never existed.
 */
export function currentBusinessPeriod(now) {
  const ms = now === undefined ? Date.now() : _toMs(now);
  const p = zonedPartsAtInstant(ms);
  return { year: p.year, month: p.month };
}

/**
 * An all-day form value → its wire form.
 *
 * An all-day task has a date and no time of day, so it is stored at UTC
 * midnight: that is the one anchor for which slicing "YYYY-MM-DD" off
 * the stored string gives the same date in every timezone on earth.
 * Feeding an all-day date through the normal wall-clock conversion
 * instead would turn 18.08. into an instant a couple of hours after
 * Swiss midnight, and any consumer reading the UTC date back off it
 * would name the 17th.
 *
 * Deliberately NOT a timezone conversion: the date the user picked is
 * already the date they meant.
 */
export function allDayInputToIso(value) {
  if (!value) return null;
  const parts = _parseWallClock(value);
  if (!parts) return null;
  return `${parts.year}-${_pad2(parts.month)}-${_pad2(parts.day)}T00:00`;
}
